import { useRequiredContext } from "@redotech/react-util/context";
import {
  PointsSummaryBlock,
  PointsSummaryButtonStyle,
} from "@redotech/redo-loyalty-common/loyalty-page-builder/definition";
import { memo } from "react";
import ReactMarkdown from "react-markdown";
import { BrandKitButton } from "../../brand-kit-button";
import { Flex } from "../../flex";
import { Text } from "../../text";
import {
  LoyaltyPageContext,
  LoyaltyPageEnvironment,
} from "../context/loyalty-page-context";

export const PointsSummary = memo(function PointsSummary({
  loggedInContent,
  loggedOutContent,
  allTime,
  pointsBalance,
  headerFontFamily,
  bodyFontFamily,
}: PointsSummaryBlock) {
  const context = useRequiredContext(LoyaltyPageContext);
  const isLoggedIn = !!context.customer;

  return (
    <Flex dir="column" gap="3xl" px="xl" py="6xl" w="full">
      <Text
        fontSize="2xl"
        fontWeight="bold"
        style={{
          color: isLoggedIn
            ? loggedInContent.titleColor
            : loggedOutContent.titleColor,
          fontFamily: headerFontFamily,
        }}
      >
        {isLoggedIn
          ? loggedInContent.title || "Your Points Summary"
          : loggedOutContent.title || "Join Our Rewards Program"}
      </Text>

      {isLoggedIn ? (
        <LoggedInContent
          allTime={allTime}
          bodyFontFamily={bodyFontFamily}
          headerFontFamily={headerFontFamily}
          loggedInContent={loggedInContent}
          pointsBalance={pointsBalance}
        />
      ) : (
        <LoggedOutContent
          bodyFontFamily={bodyFontFamily}
          loggedOutContent={loggedOutContent}
        />
      )}
    </Flex>
  );
});

const LoggedInContent = memo(function LoggedInContent({
  loggedInContent,
  allTime,
  pointsBalance,
  headerFontFamily,
  bodyFontFamily,
}: {
  loggedInContent: PointsSummaryBlock["loggedInContent"];
  allTime: PointsSummaryBlock["allTime"];
  pointsBalance: PointsSummaryBlock["pointsBalance"];
  headerFontFamily: string;
  bodyFontFamily: string;
}) {
  const context = useRequiredContext(LoyaltyPageContext);
  if (!context.customer) {
    return null;
  }

  const customer = context.customer;
  const isBuilder = context.environment === LoyaltyPageEnvironment.BUILDER;

  return (
    <Flex dir="column" gap="3xl" w="full">
      {/* Points summary in two columns */}
      <Flex dir="row" gap="3xl">
        {/* Left column - Current points (3/5 width) */}
        <Flex dir="column" style={{ flex: 3 }}>
          <Text
            fontSize="2xl"
            fontWeight="bold"
            style={{
              color: pointsBalance.color || "#000000",
              fontFamily: headerFontFamily,
            }}
          >
            {customer.currentPoints.toLocaleString()}
          </Text>
          <Text
            fontSize="sm"
            style={{ fontFamily: bodyFontFamily }}
            textColor="secondary"
          >
            Current Points
          </Text>
          <Text
            fontSize="xs"
            style={{ fontFamily: bodyFontFamily }}
            textColor="secondary"
          >
            Worth ${customer.currentMoney.toFixed(2)}
          </Text>
        </Flex>

        {/* Right column - All time stats (2/5 width) */}
        <Flex dir="column" gap="lg" style={{ flex: 2 }}>
          {allTime.pointsEarned.enabled && (
            <Flex dir="column" gap="none">
              <Text
                fontSize="lg"
                fontWeight="bold"
                style={{
                  color: allTime.pointsEarned.color,
                  fontFamily: headerFontFamily,
                }}
              >
                {customer.pointsEarned.toLocaleString()}
              </Text>
              <Text
                fontSize="xs"
                style={{ fontFamily: bodyFontFamily }}
                textColor="secondary"
              >
                All-Time Points Earned
              </Text>
            </Flex>
          )}

          {allTime.moneySpent.enabled && (
            <Flex dir="column" gap="none">
              <Text
                fontSize="lg"
                fontWeight="bold"
                style={{
                  color: allTime.moneySpent.color,
                  fontFamily: headerFontFamily,
                }}
              >
                ${customer.moneySpent.toLocaleString()}
              </Text>
              <Text
                fontSize="xs"
                style={{ fontFamily: bodyFontFamily }}
                textColor="secondary"
              >
                All-Time Money Spent
              </Text>
            </Flex>
          )}

          {allTime.pointsSpent.enabled && (
            <Flex dir="column" gap="none">
              <Text
                fontSize="lg"
                fontWeight="bold"
                style={{
                  color: allTime.pointsSpent.color,
                  fontFamily: headerFontFamily,
                }}
              >
                {customer.pointsSpent.toLocaleString()}
              </Text>
              <Text
                fontSize="xs"
                style={{ fontFamily: bodyFontFamily }}
                textColor="secondary"
              >
                All-Time Points Spent
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>

      {/* Body content */}
      {(loggedInContent.body || isBuilder) && (
        <Text
          fontSize="md"
          style={{
            color: loggedInContent.bodyColor,
            fontFamily: bodyFontFamily,
          }}
        >
          <ReactMarkdown>
            {loggedInContent.body ||
              (isBuilder
                ? "Customize this section with information about how your customers can use their points."
                : "")}
          </ReactMarkdown>
        </Text>
      )}
    </Flex>
  );
});

const LoggedOutContent = memo(function LoggedOutContent({
  loggedOutContent,
  bodyFontFamily,
}: {
  loggedOutContent: PointsSummaryBlock["loggedOutContent"];
  bodyFontFamily: string;
}) {
  const context = useRequiredContext(LoyaltyPageContext);

  const getHierarchy = () => {
    switch (loggedOutContent.buttonStyle) {
      case PointsSummaryButtonStyle.FILLED:
        return "primary" as const;
      case PointsSummaryButtonStyle.OUTLINED:
        return "secondary" as const;
      case PointsSummaryButtonStyle.GHOST:
        return "tertiary" as const;
    }
  };

  const hierarchy = getHierarchy();

  const createButtonOverride = () => ({
    ...context.teamSettings.brandKit.buttons[hierarchy],
    backgroundColor:
      loggedOutContent.buttonStyle === PointsSummaryButtonStyle.FILLED
        ? loggedOutContent.buttonBackgroundColor
        : undefined,
    textColor: loggedOutContent.buttonTextColor,
    border: {
      ...context.teamSettings.brandKit.buttons[hierarchy].border,
      cornerRadiusPx: loggedOutContent.buttonBorderRadius,
      stroke: {
        ...context.teamSettings.brandKit.buttons[hierarchy].border.stroke,
        color: loggedOutContent.buttonBorderColor,
        weightPx:
          loggedOutContent.buttonStyle === PointsSummaryButtonStyle.GHOST
            ? 0
            : 1,
      },
    },
    font: {
      ...context.teamSettings.brandKit.font.hierarchy.body,
      fontFamily: bodyFontFamily,
    },
  });

  const buttonOverride = {
    primary: createButtonOverride(),
    secondary: createButtonOverride(),
    tertiary: createButtonOverride(),
  };

  return (
    <Flex dir="column" gap="lg">
      <Text
        fontSize="md"
        style={{
          color: loggedOutContent.bodyColor,
          fontFamily: bodyFontFamily,
        }}
      >
        <ReactMarkdown>
          {loggedOutContent.body ||
            "Sign up to start earning points with every purchase."}
        </ReactMarkdown>
      </Text>

      <Flex>
        <BrandKitButton
          brandKit={context.teamSettings.brandKit}
          globalOverride={buttonOverride}
          hierarchy={hierarchy}
          onClick={
            loggedOutContent.buttonLink
              ? () => {
                  window.open(loggedOutContent.buttonLink, "_blank");
                }
              : undefined
          }
          text={loggedOutContent.buttonText || "Sign Up for Rewards"}
        />
      </Flex>
    </Flex>
  );
});
